// Reuses signed Storage URLs while they are still valid. A new signature changes
// the URL, which defeats the browser cache and downloads the file again.

export const signedUrlTtlSeconds = 60 * 60
const renewBeforeMs = 10 * 60 * 1000

export type SignUrls = (bucket: string, paths: string[], expiresIn: number) => Promise<Array<string | undefined>>

export function createSignedUrlCache(sign: SignUrls, now = () => Date.now()) {
  const entries = new Map<string, { url: string; expiresAt: number }>()
  const key = (bucket: string, path: string) => `${bucket}/${path}`

  return {
    // Resolves to a map with an entry for every path that could be signed;
    // missing objects are left out and are not cached.
    async get(bucket: string, paths: string[]) {
      const urls = new Map<string, string>()
      const pending: string[] = []
      for (const path of new Set(paths)) {
        const entry = entries.get(key(bucket, path))
        if (entry && entry.expiresAt - now() > renewBeforeMs) urls.set(path, entry.url)
        else pending.push(path)
      }
      if (pending.length) {
        const expiresAt = now() + signedUrlTtlSeconds * 1000
        const signed = await sign(bucket, pending, signedUrlTtlSeconds)
        pending.forEach((path, index) => {
          const url = signed[index]
          if (!url) return
          entries.set(key(bucket, path), { url, expiresAt })
          urls.set(path, url)
        })
      }
      return urls
    },
  }
}
